import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Navbar } from '../../components/feature/Navbar';
import { propertyDetails } from '../../mocks/propertyDetails';
import { ImageGallery } from './components/ImageGallery';
import { PropertySpecs } from './components/PropertySpecs';
import { AgentCard } from './components/AgentCard';
import { BookingForm } from './components/BookingForm';
import { NearbyMap } from './components/NearbyMap';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);

  // Ensure id is a string before comparison (useParams returns string | undefined)
  const property = propertyDetails.find(p => p.id === id) || propertyDetails[0];

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const text = `Check out this property: ${property.title}`;

    try {
      switch (platform) {
        case 'whatsapp':
          window.open(
            `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
            '_blank',
            'noopener,noreferrer'
          );
          break;
        case 'facebook':
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
            '_blank',
            'noopener,noreferrer'
          );
          break;
        case 'twitter':
          window.open(
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(
              text
            )}&url=${encodeURIComponent(url)}`,
            '_blank',
            'noopener,noreferrer'
          );
          break;
        case 'copy':
          navigator.clipboard
            .writeText(url)
            .then(() => alert('Link copied to clipboard!'))
            .catch(() => alert('Failed to copy link.'));
          break;
        default:
          console.warn(`Unsupported share platform: ${platform}`);
      }
    } catch (e) {
      console.error('Error while sharing:', e);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-6 lg:px-12 py-4">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 hover:text-coral-500 cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back
          </button>
          <span className="text-gray-300">|</span>
          <Link to="/" className="hover:text-coral-500 transition-colors cursor-pointer">
            Home
          </Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/listings" className="hover:text-coral-500 transition-colors cursor-pointer">
            Listings
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-900 font-medium">{property.title}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 lg:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900">{property.title}</h1>
                    {property.ribbon && (
                      <span className="bg-gray-800 text-white px-4 py-1 text-xs font-bold tracking-wider rounded">
                        {property.ribbon}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center text-gray-600 space-x-2">
                    <i className="ri-map-pin-line" />
                    <span>
                      {property.location}, {property.address}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-coral-500">
                    {property.currency}
                    {property.price.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">
                    {property.type === 'rental' ? 'per month' : 'total price'}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowBookingForm(true)}
                  className="flex-1 bg-coral-500 hover:bg-coral-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  Schedule Viewing
                </button>
                <button
                  onClick={() => setIsFavorite(!isFavorite)}
                  className={`w-12 h-12 flex items-center justify-center rounded-lg border-2 transition-all cursor-pointer ${
                    isFavorite
                      ? 'bg-coral-500 border-coral-500 text-white'
                      : 'border-gray-300 text-gray-600 hover:border-coral-500'
                  }`}
                >
                  <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-xl`} />
                </button>
                <div className="relative group">
                  <button className="w-12 h-12 flex items-center justify-center rounded-lg border-2 border-gray-300 text-gray-600 hover:border-coral-500 transition-colors cursor-pointer">
                    <i className="ri-share-line text-xl" />
                  </button>
                  <div className="absolute right-0 top-14 bg-white rounded-lg shadow-xl border border-gray-200 p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20 w-48">
                    <button
                      onClick={() => handleShare('whatsapp')}
                      className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-gray-50 rounded-md transition-colors cursor-pointer text-left"
                    >
                      <i className="ri-whatsapp-line text-green-600 text-lg" />
                      <span className="text-sm text-gray-700">WhatsApp</span>
                    </button>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-gray-50 rounded-md transition-colors cursor-pointer text-left"
                    >
                      <i className="ri-facebook-fill text-blue-600 text-lg" />
                      <span className="text-sm text-gray-700">Facebook</span>
                    </button>
                    <button
                      onClick={() => handleShare('twitter')}
                      className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-gray-50 rounded-md transition-colors cursor-pointer text-left"
                    >
                      <i className="ri-twitter-fill text-blue-400 text-lg" />
                      <span className="text-sm text-gray-700">Twitter</span>
                    </button>
                    <button
                      onClick={() => handleShare('copy')}
                      className="w-full flex items-center space-x-3 px-4 py-2 hover:bg-gray-50 rounded-md transition-colors cursor-pointer text-left"
                    >
                      <i className="ri-file-copy-line text-gray-600 text-lg" />
                      <span className="text-sm text-gray-700">Copy Link</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Image Gallery */}
            <ImageGallery images={property.gallery} title={property.title} />

            {/* Property Specifications */}
            <PropertySpecs property={property} />

            {/* Description */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Description</h2>
              <div className="text-gray-700 leading-relaxed space-y-4">
                <p>{property.description}</p>
              </div>
            </div>

            {/* Features & Amenities */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Features &amp; Amenities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {property.amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-lg">
                      <i className={`${amenity.icon} text-teal-600 text-lg`} />
                    </div>
                    <span className="text-sm text-gray-700">{amenity.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Location & Map */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Location &amp; Nearby</h2>
              <NearbyMap location={property.location} coordinates={property.coordinates} />

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {property.nearby.map((place, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg">
                        <i className={`${place.icon} text-gray-700 text-lg`} />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{place.name}</span>
                    </div>
                    <span className="text-sm text-gray-600">{place.distance}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Virtual Tour */}
            {property.virtualTour && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Virtual Tour</h2>
                <div className="relative w-full h-96 bg-gray-100 rounded-lg overflow-hidden">
                  <iframe
                    src={property.virtualTour}
                    className="w-full h-full"
                    allowFullScreen
                    title="Virtual Tour"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Agent Card */}
            <AgentCard agent={property.agent} />

            {/* Quick Info */}
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Property ID</h3>
              <p className="text-sm text-gray-600 mb-6">{property.propertyId}</p>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Property Type</span>
                  <span className="font-medium text-gray-900">{property.propertyType}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Status</span>
                  <span className="font-medium text-green-600">Available</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Year Built</span>
                  <span className="font-medium text-gray-900">{property.yearBuilt}</span>
                </div>
              </div>

              <button
                onClick={() => setShowBookingForm(true)}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Information
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <BookingForm 
          property={{
            title: property.title,
            propertyId: id || property.id
          }} 
          onClose={() => setShowBookingForm(false)} 
        />
      )}

      {/* Footer */}
      <footer className="bg-teal-600 text-white py-12 px-6 lg:px-12 mt-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div>
            <h3 className="text-lg font-bold mb-4">About JUWA</h3>
            <p className="text-sm text-white/90 leading-relaxed">
              Your trusted partner in finding the perfect home. We connect buyers, renters, and
              sellers with premium properties across the region.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="hover:text-coral-300 transition-colors cursor-pointer">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/listings" className="hover:text-coral-300 transition-colors cursor-pointer">
                  Listings
                </Link>
              </li>
              <li>
                <a href="/agents" className="hover:text-coral-300 transition-colors cursor-pointer">
                  Agents
                </a>
              </li>
              <li>
                <a href="/about" className="hover:text-coral-300 transition-colors cursor-pointer">
                  About Us
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-bold mb-4">Contact</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center space-x-2">
                <i className="ri-phone-line" />
                <span>+254 700 000 000</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-mail-line" />
                <span>info@juwa.com</span>
              </li>
              <li className="flex items-center space-x-2">
                <i className="ri-map-pin-line" />
                <span>Nairobi, Kenya</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-bold mb-4">Follow Us</h3>
            <div className="flex space-x-4">
              <a
                href="#"
                className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-xl" />
              </a>
              <a
                href="#"
                className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-twitter-fill text-xl" />
              </a>
              <a
                href="#"
                className="w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line text-xl" />
              </a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/20 pt-6 text-center text-sm">
          <p>
            &copy; 2025 JUWA Real Estate. All rights reserved. |{' '}
            <a
              href="https://readdy.ai/?ref=logo"
              className="hover:text-coral-300 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PropertyDetailPage;
